'use client';

import { PropsWithChildren, ReactNode, useCallback, useMemo, useState } from 'react';
import { Component } from '@headless-workspace/typings';
import { ToastProvider } from '../toastProvider';
import {
    ModalActionsContext,
    ModalStateContext,
    SideMenuModal,
    SideMenuModalList,
    ModalActionsContextType,
    ModalStateContextType,
} from './ModalContexts';

export type ModalProviderProps = PropsWithChildren;
const Z_INDEX_BASE = 6;
const Z_INDEX_STEP = 2;

const TRANSITION_DURATION_MS = 400; // transitionDuration slow1 from tailwind theme

const getModalZIndex = (menuModals: SideMenuModalList) => {
    const openModalsCount = Object.values(menuModals).filter((modal) => modal.isOpen).length;
    return Z_INDEX_BASE + openModalsCount * Z_INDEX_STEP;
};

export const ModalProvider: Component<ModalProviderProps> = ({ children }) => {
    const [menuModals, setMenuModals] = useState<SideMenuModalList>({});

    const openMenuModal = useCallback((id: string, content?: ReactNode) => {
        setMenuModals((prevMenuModals) => {
            const zIndex = getModalZIndex(prevMenuModals);

            return {
                ...prevMenuModals,
                [id]: {
                    ...prevMenuModals[id],
                    zIndex,
                    isOpen: true,
                    map: { ...prevMenuModals[id]?.map, default: content ?? prevMenuModals[id]?.map.default },
                },
            };
        });
    }, []);

    const addMenuModal = useCallback((id: string, menu: SideMenuModal) => {
        setMenuModals((prevMenuModals) => ({ ...prevMenuModals, [id]: { ...menu, isOpen: false } }));
    }, []);

    const addAndOpenMenuModal = useCallback((id: string, menu: SideMenuModal) => {
        setMenuModals((prev) => {
            const zIndex = getModalZIndex(prev);

            const modalWithZIndex = { ...menu, isOpen: false, zIndex };
            const newState = { ...prev, [id]: modalWithZIndex };

            requestAnimationFrame(() => {
                setMenuModals((currentPrev) => ({
                    ...currentPrev,
                    [id]: { ...modalWithZIndex, isOpen: true },
                }));
            });

            return newState;
        });
    }, []);

    const addIfAbsentAndOpenMenuModal = useCallback((id: string, menu: SideMenuModal, override = true) => {
        setMenuModals((prev) => {
            const zIndex = getModalZIndex(prev);

            const shouldOverride = override || !prev[id];
            const modal = shouldOverride ? { ...menu, isOpen: false, zIndex } : { ...prev[id], isOpen: false, zIndex };

            const newState = {
                ...prev,
                [id]: modal,
            };

            requestAnimationFrame(() => {
                setMenuModals((currentPrev) => ({
                    ...currentPrev,
                    [id]: { ...modal, isOpen: true },
                }));
            });

            return newState;
        });
    }, []);

    const closeMenuModal = useCallback((id: string, remove = false) => {
        setMenuModals((prevMenuModals) => {
            if (!prevMenuModals[id]) {
                return prevMenuModals;
            }

            const updatedMenuModals = { ...prevMenuModals };

            if (prevMenuModals[id]?.onClose) {
                prevMenuModals[id].onClose();
            }

            updatedMenuModals[id] = { ...prevMenuModals[id], isOpen: false, navigationStack: [] };

            return updatedMenuModals;
        });

        if (remove) {
            // wait for close animation before remove modal
            setTimeout(() => {
                setMenuModals((prevMenuModals) => {
                    const { [id]: toRemove, ...filteredMenuModals } = prevMenuModals;

                    return filteredMenuModals;
                });
            }, TRANSITION_DURATION_MS);
        }
    }, []);

    const navigateMenuModal = useCallback((id: string, contentId: string, content?: ReactNode) => {
        setMenuModals((prevMenuModals) => {
            if (!prevMenuModals[id]) {
                return prevMenuModals;
            }

            return {
                ...prevMenuModals,
                [id]: {
                    ...prevMenuModals[id],
                    navigationStack: [...prevMenuModals[id].navigationStack, contentId],
                    map: { ...prevMenuModals[id].map, [contentId]: content ?? prevMenuModals[id].map[contentId] },
                },
            };
        });
    }, []);

    const goBackMenuModal = useCallback((id: string) => {
        setMenuModals((prevMenuModals) => {
            if (!prevMenuModals[id]) {
                return prevMenuModals;
            }

            return {
                ...prevMenuModals,
                [id]: { ...prevMenuModals[id], navigationStack: prevMenuModals[id].navigationStack.slice(0, -1) },
            };
        });
    }, []);

    // Memoize stable actions (these never change)
    const actionsValue = useMemo<ModalActionsContextType>(
        () => ({
            open: openMenuModal,
            add: addMenuModal,
            addAndOpen: addAndOpenMenuModal,
            addIfAbsentAndOpen: addIfAbsentAndOpenMenuModal,
            close: closeMenuModal,
            navigate: navigateMenuModal,
            goBack: goBackMenuModal,
            hydrate: setMenuModals,
        }),
        [
            openMenuModal,
            addMenuModal,
            addAndOpenMenuModal,
            addIfAbsentAndOpenMenuModal,
            closeMenuModal,
            navigateMenuModal,
            goBackMenuModal,
        ],
    );

    // Memoize state values (these change but are computed efficiently)
    const stateValue = useMemo<ModalStateContextType>(() => {
        const openKeys = Object.keys(menuModals).filter((key) => menuModals[key].isOpen);
        const modalIdInView = openKeys[openKeys.length - 1] || undefined;
        const hasOpenModals = openKeys.length > 0;
        const isMenuModalOpen = (id: string) => {
            return menuModals[id]?.isOpen ?? false;
        };
        return {
            sideMenuModalList: menuModals,
            isOpen: hasOpenModals,
            isMenuModalOpen,
            modalIdInView,
        };
    }, [menuModals]);

    return (
        <ModalActionsContext.Provider value={actionsValue}>
            <ModalStateContext.Provider value={stateValue}>
                <ToastProvider>{children}</ToastProvider>
            </ModalStateContext.Provider>
        </ModalActionsContext.Provider>
    );
};
